import React from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, SlidersHorizontal, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { useConfirm } from '@/hooks/use-confirm';
import {
  useReportControls, ColumnHeader, SearchDrawer, ColumnsToggle, type ColumnConfig,
} from '@/components/shared/report-controls';

export function RolesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<any>(null);
  const [form, setForm] = React.useState<any>({ name: '' });

  const columns: ColumnConfig[] = React.useMemo(() => [
    { key: 'name', label: 'Name', filterType: 'text' },
  ], []);
  const ctl = useReportControls({ namespace: '/roles', columns });

  const { data, isLoading } = useQuery({
    queryKey: ['/roles', ctl.queryParams],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '200' });
      for (const [k, v] of Object.entries(ctl.queryParams)) p.set(k, v);
      return (await api<any[]>(`/roles?${p.toString()}`)).data || [];
    },
  });

  const save = useMutation({
    mutationFn: async (body: any) => {
      const url = editing ? `/roles/${editing.id}` : '/roles';
      const method = editing ? 'PUT' : 'POST';
      const r = await api(url, { method, body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Role saved', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/roles'] }); setOpen(false); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/roles/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Role deleted', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/roles'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function openNew() { setEditing(null); setForm({ name: '' }); setOpen(true); }
  function openEdit(row: any) { setEditing(row); setForm({ name: row.name ?? '' }); setOpen(true); }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        {open ? (
          <div className="flex items-center gap-3 min-w-0">
            <Button size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Back to list">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold truncate">{editing ? 'Edit Role' : 'New Role'}</h1>
          </div>
        ) : (
          <h1 className="text-2xl font-semibold">Roles &amp; Permissions</h1>
        )}
        {!open && <Button onClick={openNew}><Plus className="h-4 w-4" /> New Role</Button>}
      </div>

      {!open && (<>
      <div className="flex items-center gap-2 flex-wrap">
        {ctl.activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={() => ctl.setFilters({})} className="text-xs">
            Clear filters ({ctl.activeFilterCount})
          </Button>
        )}
        {ctl.hasNonDefaultView && (
          <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={ctl.resetView}>
            Reset view
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => ctl.setDrawerOpen(true)}>
          <SlidersHorizontal className="h-4 w-4 mr-1" /> Search
        </Button>
        <ColumnsToggle ctl={ctl} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {ctl.visibleColumns.map((col) => (
                  <TableHead key={col.key} className="p-0">
                    <ColumnHeader {...ctl.headerProps(col)} />
                  </TableHead>
                ))}
                <TableHead className="w-40 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const colSpan = ctl.visibleColumns.length + 1;
                if (isLoading) return <TableRow><TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>;
                if (data?.length === 0) return <TableRow><TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">No roles</TableCell></TableRow>;
                return null;
              })()}
              {data?.map((r: any) => (
                <TableRow key={r.id}>
                  {ctl.visibleColumns.map((col) => (
                    <TableCell key={col.key} className={col.key === 'name' ? 'font-medium' : ''}>
                      {col.render ? col.render(r) : (col.key.split('.').reduce((a: any, k) => a?.[k], r) ?? '—')}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/roles/${r.id}`}>Permissions</Link>
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={async () => { if (await confirm({ title: 'Delete this role?', destructive: true, confirmLabel: 'Delete' })) del.mutate(r.id); }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>)}

      {open && (
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <p className="text-xs text-muted-foreground">
              Per-resource permissions and custom actions (including the
              former &quot;Change Service Type&quot; toggle) are managed from the
              Permissions page.
            </p>
            <div className="flex flex-wrap gap-2 pt-4 border-t justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => save.mutate(form)} disabled={save.isPending}>{editing ? 'Update' : 'Create'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {ctl.drawerOpen && <SearchDrawer {...ctl.drawerProps} />}
    </div>
  );
}
